/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import { Inject, Injectable } from '@angular/core';

import {
    HttpMethod,
    HttpWrapper,
    THttpWrapper,
} from 'ajs/modules/core/factories/http-wrapper';
import {
    IVsLogListResponseData,
    IVsLogsGraphResponseData,
    IVsLogsParams,
    IVsLogsStateParams,
    TVsLogsApiResponseData,
    TVsLogsConstantParams,
} from '../vs-logs.types';
import {
    buildUrlFromParams,
    convertStateToApiParams,
} from '../utils/vs-logs.utils';

const VS_LOGS_CONSTANT_PARAMS: TVsLogsConstantParams = {
    js_compat: true,
};

export const LOGS_API_URL = '/api/analytics/logs';

/**
 * Initial and max timeouts for logs API requests.
 */
export const LOGS_REQUEST_INITIAL_TIMEOUT = 2;
export const LOGS_REQUEST_MAX_TIMEOUT = 32;

/**
 * Max amount of API requests we make.
 */
export const LOGS_MAX_REQUEST_COUNT = 10;

/**
 * @description Service used for API requests for the various VS Logs Components.
 * @author Alex Klyuev, Zhiqian Liu
 */
@Injectable()
export class VsLogsApiRequestService {
    private readonly httpWrapper: HttpWrapper;

    constructor(@Inject(HttpWrapper) HttpWrapperClass: THttpWrapper) {
        this.httpWrapper = new HttpWrapperClass();
    }

    /**
     * Download logs. Requesting API with download=true will automatically
     * download a CSV of the data.
     */
    public static downloadLogs(stateParams: IVsLogsStateParams): void {
        const params = convertStateToApiParams(stateParams, VS_LOGS_CONSTANT_PARAMS);

        params.download = true;

        if (params.end) {
            const endStringified = JSON.stringify(params.end);

            params.end = endStringified.slice(1, endStringified.length - 1);
        }

        // remove undefined parameters
        Object.entries(params).forEach(([param, value]) => {
            if (value === undefined) {
                delete params[param];
            }
        });

        const url = buildUrlFromParams(params);

        window.location.assign(url);
    }

    /**
     * Get logs from API.
     */
    public requestLogs<
        T extends IVsLogListResponseData | IVsLogsGraphResponseData
    >(
        stateParams: IVsLogsStateParams,
        requestId: string,
    ): Promise<T> {
        const params = convertStateToApiParams(stateParams, VS_LOGS_CONSTANT_PARAMS);

        return this.fetchApiData<T>(params, requestId);
    }

    /**
     * Request logs from API with a timeout increase loop until all data is returned
     * or a max number of calls is reached.
     */
    public requestLogsWithTimeoutIncrease<
        T extends IVsLogListResponseData | IVsLogsGraphResponseData
    >(
        stateParams: IVsLogsStateParams,
        requestId: string,
        endingCondition?: (data: T) => boolean,
    ): Promise<T> {
        const params = convertStateToApiParams(stateParams, VS_LOGS_CONSTANT_PARAMS);

        return this.requestLoop<T>(params, requestId, endingCondition);
    }

    /**
     * Cancel request of a given requestId.
     */
    public cancelRequest(requestId: string): void {
        if (this.httpWrapper.requestsHash[requestId]) {
            this.httpWrapper.cancelRequest(requestId);
        }
    }

    /**
     * Method to make an API request to the logs endpoint and return data.
     */
    private async fetchApiData<T extends TVsLogsApiResponseData>(
        params: IVsLogsParams,
        requestId: string,
    ): Promise<T> {
        const { data } = await this.httpWrapper.request({
            url: LOGS_API_URL,
            method: HttpMethod.GET,
            params,
            requestId,
        });

        return data;
    }

    /**
     * Make logs requests with exponentially increasing timeout (up to 32),
     * until all data is returned, 10 requests have been made,
     * or a custom condition is met (whichever is sooner).
     */
    private async requestLoop<T extends TVsLogsApiResponseData>(
        params: IVsLogsParams,
        requestId: string,
        endingCondition?: (data: T) => boolean,
        requestCount = 1,
    ): Promise<T> {
        // if all data isn't returned after 10 timeouts, handle as though API error
        if (requestCount === LOGS_MAX_REQUEST_COUNT) {
            throw new Error('API timeout');
        }

        if (!params.timeout) {
            params.timeout = LOGS_REQUEST_INITIAL_TIMEOUT;
        }

        const data = await this.fetchApiData<T>(params, requestId);

        if (data.percent_remaining === 0 || endingCondition && endingCondition(data)) {
            return data;
        } else {
            if (params.timeout < LOGS_REQUEST_MAX_TIMEOUT) {
                params.timeout *= 2;
            }

            requestCount++;

            return this.requestLoop(
                params,
                requestId,
                endingCondition,
                requestCount,
            );
        }
    }
}
